import { apiClient } from "./apiClient";
import { BrazilDate } from "../utils/BrazilDate";

export interface FilaEntrega {
  id?: number;
  dataFila: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  lojaId: number;
  entregadorId: number;
  loja?: {
    id: number;
    nome: string;
  };
  entregador?: {
    id: number;
    nome: string;
    email: string;
  };
}

export interface FilaEntregaFilters {
  lojaId?: number;
  entregadorId?: number;
  status?: string;
  statusIn?: string[];
  dataInicio?: string;
  dataFim?: string;
  page?: number;
  limit?: number;
}

export interface PaginatedFilaEntregaResponse {
  data: FilaEntrega[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface CreateFilaEntregaData {
  dataFila: string;
  status?: string;
  lojaId: number;
  entregadorId: number;
}

export interface UpdateFilaEntregaData {
  status?: string;
  dataFila?: string;
}

const FILA_ENTREGA_PAGE_SIZE = 200;

function appendFilaEntregaQueryParams(
  params: URLSearchParams,
  filters: FilaEntregaFilters
): void {
  if (filters.lojaId != null) {
    params.append("lojaId", filters.lojaId.toString());
  }
  if (filters.entregadorId != null) {
    params.append("entregadorId", filters.entregadorId.toString());
  }
  if (filters.status) {
    params.append("status", filters.status);
  }
  if (filters.statusIn?.length) {
    params.set("statusIn", filters.statusIn.join(","));
  }
  if (filters.dataInicio) {
    params.append("dataInicio", filters.dataInicio);
  }
  if (filters.dataFim) {
    params.append("dataFim", filters.dataFim);
  }
  if (filters.page != null) {
    params.append("page", filters.page.toString());
  }
  if (filters.limit != null) {
    params.append("limit", filters.limit.toString());
  }
}

export function buildFilaEntregaDiaAtualFilters(
  lojaId: number,
  options?: { status?: string; statusIn?: string[] }
): FilaEntregaFilters {
  const hoje = BrazilDate.operationalTodayISO();
  return {
    lojaId,
    dataInicio: hoje,
    dataFim: hoje,
    limit: FILA_ENTREGA_PAGE_SIZE,
    page: 1,
    ...options,
  };
}

export const filaEntregaService = {
  getFilaEntregasPaginated: async (
    filters?: FilaEntregaFilters
  ): Promise<PaginatedFilaEntregaResponse> => {
    const params = new URLSearchParams();
    if (filters) {
      appendFilaEntregaQueryParams(params, filters);
    }
    const url = `/filas-entregas${params.toString() ? `?${params.toString()}` : ""}`;
    return apiClient.get(url);
  },

  getFilaEntregas: async (
    filters?: FilaEntregaFilters
  ): Promise<FilaEntrega[]> => {
    const pageSize = filters?.limit ?? FILA_ENTREGA_PAGE_SIZE;
    const linhas: FilaEntrega[] = [];
    let page = filters?.page ?? 1;
    let totalPages = 1;

    while (page <= totalPages) {
      const res = await filaEntregaService.getFilaEntregasPaginated({
        ...filters,
        page,
        limit: pageSize,
      });
      linhas.push(...res.data);
      totalPages = res.totalPages;
      page += 1;
    }

    return linhas;
  },

  getFilaEntregaById: async (id: number): Promise<FilaEntrega> => {
    return apiClient.get(`/filas-entregas/${id}`);
  },

  createFilaEntrega: async (
    data: CreateFilaEntregaData
  ): Promise<FilaEntrega> => {
    return apiClient.post("/filas-entregas", data);
  },

  updateFilaEntrega: async (
    id: number,
    data: UpdateFilaEntregaData
  ): Promise<FilaEntrega> => {
    return apiClient.put(`/filas-entregas/${id}`, data);
  },

  deleteFilaEntrega: async (id: number): Promise<void> => {
    return apiClient.delete(`/filas-entregas/${id}`);
  },

  chamarEntregador: async (filaId: number): Promise<{ message: string }> => {
    return apiClient.post(`/filas-entregas/${filaId}/chamar`, {});
  },
};
